import type { ReactNode } from 'react';
import { Card, CardActionArea, Typography, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import type { StatusTone } from '@/core/utils/statusTone';
import { HelpTooltip } from '@/shared';

interface KpiCardProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  /**
   * Semantic tint for the icon tile. Defaults to `primary`. Use this to make a
   * row of KPIs readable at a glance — urgent counts in `error`, pending in
   * `warning`, healthy in `success`.
   */
  tone?: StatusTone;
  /**
   * When provided, the entire card surface becomes a router link with MUI's
   * CardActionArea hover/focus affordance. When omitted, the card is read-only
   * — no cursor change, no hover tint — preserving the original behavior for
   * sections whose drill-down targets require date math not yet computed here.
   */
  linkTo?: string;
  /**
   * Explains what the number means, on an `(i)` beside the label.
   *
   * Added for the money tiles: "Amount $4,150" tells you nothing about which
   * jobs it covers or whether it is quoted or won. A dispatcher should not have
   * to read the BLL to find out.
   */
  helpText?: string;
  helpAriaLabel?: string;
}

/**
 * KPI tile for dashboard sections.
 *
 * Layout follows the `.kpi` block in mockup `03-web-dispatch` / `04-admin-portal`:
 * a tinted rounded icon tile on the left, then the label above a large tabular
 * value. Left-aligned — a row of these scans as a column of numbers, which the
 * old centered layout did not.
 *
 * Pass `linkTo` to make the card clickable (router navigation).
 */
export default function KpiCard({
  label,
  value,
  icon,
  tone = 'primary',
  linkTo,
  helpText,
  helpAriaLabel,
}: KpiCardProps) {
  const content = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.875,
        py: 1.75,
        width: '100%',
      }}
    >
      {icon && (
        <Box
          aria-hidden
          sx={(theme) => {
            // `neutral` has no palette slot — borrow the secondary text ramp.
            const base =
              tone === 'neutral'
                ? theme.palette.text.secondary
                : theme.palette[tone].main;
            return {
              width: 38,
              height: 38,
              borderRadius: '11px',
              flex: 'none',
              display: 'grid',
              placeItems: 'center',
              color: base,
              backgroundColor: alpha(base, theme.palette.mode === 'dark' ? 0.22 : 0.12),
              '& .MuiSvgIcon-root': { fontSize: '1.1875rem' },
            };
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Typography
            component="p"
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: 'text.secondary',
              lineHeight: 1.4,
            }}
          >
            {label}
          </Typography>
          {helpText && (
            <HelpTooltip title={helpText} ariaLabel={helpAriaLabel ?? helpText} placement="top" />
          )}
        </Box>
        <Typography
          component="p"
          sx={{
            fontSize: '1.4375rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            // Tabular figures keep a row of KPIs optically aligned.
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.25,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Card sx={{ height: '100%', display: 'flex' }}>
      {linkTo !== undefined ? (
        <CardActionArea component={RouterLink} to={linkTo} sx={{ height: '100%' }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}
